import React, { useState, useEffect } from 'react';
import { X, Download, ExternalLink, RefreshCw } from 'lucide-react';

const OfficeViewer = ({ fileUrl, fileName, onClose, token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerUrl, setViewerUrl] = useState(null);
  const [publicUrl, setPublicUrl] = useState(null);
  const [currentViewer, setCurrentViewer] = useState('google'); // 'microsoft' or 'google'
  const [iframeError, setIframeError] = useState(false);
  
  // Check if file is PowerPoint
  const isPowerPoint = fileName.toLowerCase().match(/\.(ppt|pptx)$/);

  useEffect(() => {
    const setupViewer = async () => {
      console.log('========== OFFICE VIEWER SETUP START ==========');
      console.log('File Name:', fileName);
      console.log('File URL:', fileUrl);
      console.log('Current Viewer:', currentViewer);
      console.log('Has Token:', !!token);
      
      try {
        let backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        console.log('Backend URL from env:', backendUrl);
        
        // If backendUrl is empty and fileUrl contains a full URL, extract the base URL
        if (!backendUrl && fileUrl.startsWith('http')) {
          const url = new URL(fileUrl);
          // Extract base URL including the path prefix (e.g., /pandvah-backend)
          const pathParts = url.pathname.split('/api/')[0]; // Get everything before /api/
          backendUrl = `${url.origin}${pathParts}`;
          console.log('📍 Extracted backend URL from fileUrl:', backendUrl);
        }
        console.log('Final Backend URL:', backendUrl);
        
        let presignedUrl = null;
        let s3Key = null;
        
        // Step 1: Check if fileUrl is already a direct S3 presigned URL
        if (fileUrl.includes('amazonaws.com') || fileUrl.includes('X-Amz-Algorithm')) {
          console.log('✅ STEP 1: Direct S3 URL detected, using as-is');
          presignedUrl = fileUrl;
        } else {
          console.log('⏭️  STEP 1: Not a direct S3 URL, extracting s3_key...');
          
          // Step 2: Extract s3_key from the fileUrl (it's in the query parameter)
          const urlParams = new URLSearchParams(fileUrl.split('?')[1]);
          s3Key = urlParams.get('s3_key');
          
          console.log('📝 STEP 2: Extracted s3_key:', s3Key);
          
          if (!s3Key) {
            console.error('❌ No s3_key found in URL:', fileUrl);
            throw new Error('Invalid file URL - no s3_key parameter found');
          }

          // Step 3: Try to get presigned URL from backend endpoint
          const viewerEndpoint = `${backendUrl}/api/records/office-viewer-url?s3_key=${encodeURIComponent(s3Key)}`;
          console.log('🔗 STEP 3: Calling backend endpoint:', viewerEndpoint);
          
          try {
            const response = await fetch(viewerEndpoint, {
              headers: token ? {
                'Authorization': `Bearer ${token}`
              } : {}
            });

            console.log('📡 Backend response status:', response.status);
            console.log('📡 Backend response ok:', response.ok);

            if (!response.ok) {
              const errorText = await response.text();
              console.error('❌ Viewer URL error:', response.status, errorText);
              throw new Error(`Backend endpoint failed (${response.status}): ${errorText}`);
            }

            const responseText = await response.text();
            console.log('📄 Backend response text (first 200 chars):', responseText.substring(0, 200));
            
            // Try to parse as JSON
            let data;
            try {
              data = JSON.parse(responseText);
              console.log('✅ Backend response data:', data);
            } catch (parseError) {
              console.error('❌ Failed to parse response as JSON');
              console.error('Response was HTML, not JSON. Endpoint might not exist or returned error page.');
              throw new Error(`Backend returned HTML instead of JSON. Response preview: ${responseText.substring(0, 100)}`);
            }
            
            presignedUrl = data.url;
            console.log('✅ STEP 3: Successfully obtained presigned URL (length:', presignedUrl?.length, ')');
          } catch (backendError) {
            console.error('❌ STEP 3 FAILED: Backend endpoint error:', backendError);
            console.error('Error details:', {
              message: backendError.message,
              stack: backendError.stack
            });
            
            // Step 4: Fallback - try to fetch the file through the proxy endpoint
            console.log('🔄 STEP 4: Attempting fallback to proxy endpoint...');
            try {
              const proxyResponse = await fetch(fileUrl, {
                headers: token ? {
                  'Authorization': `Bearer ${token}`
                } : {},
                method: 'HEAD' // Just check if accessible
              });
              
              console.log('📡 Proxy response status:', proxyResponse.status);
              
              if (proxyResponse.ok) {
                console.error('⚠️  Proxy URL available but not compatible with external viewers');
                throw new Error('Proxy URL available but not compatible with external viewers. Please check S3 configuration.');
              }
            } catch (proxyError) {
              console.error('❌ Proxy endpoint also failed:', proxyError);
            }
            
            throw new Error(`Unable to get document URL. Backend endpoint error: ${backendError.message}`);
          }
        }
        
        if (!presignedUrl) {
          console.error('❌ No presigned URL available after all attempts');
          throw new Error('No valid document URL available');
        }
        
        console.log('✅ Setting public URL and updating viewer...');
        setPublicUrl(presignedUrl);
        
        // Set viewer URL based on current viewer choice
        updateViewerUrl(presignedUrl, currentViewer);
        setLoading(false);
        console.log('========== OFFICE VIEWER SETUP SUCCESS ==========');
      } catch (err) {
        console.error('========== OFFICE VIEWER SETUP FAILED ==========');
        console.error('Error setting up viewer:', err);
        console.error('Error message:', err.message);
        console.error('Error stack:', err.stack);
        setError(err.message);
        setLoading(false);
      }
    };

    setupViewer();
  }, [fileUrl, token, currentViewer]);

  const updateViewerUrl = (url, viewer) => {
    console.log('🔄 updateViewerUrl called with viewer:', viewer);
    console.log('🔗 Public URL (first 100 chars):', url?.substring(0, 100));
    
    const encodedPublicUrl = encodeURIComponent(url);
    
    if (viewer === 'microsoft') {
      // Microsoft Office Online Viewer
      const msViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedPublicUrl}`;
      console.log('🖼️  Setting Microsoft Viewer URL (length:', msViewerUrl.length, ')');
      setViewerUrl(msViewerUrl);
    } else if (viewer === 'google') {
      // Google Docs Viewer
      const googleViewerUrl = `https://docs.google.com/viewer?url=${encodedPublicUrl}&embedded=true`;
      console.log('🖼️  Setting Google Viewer URL (length:', googleViewerUrl.length, ')');
      setViewerUrl(googleViewerUrl);
    }
  };

  const switchViewer = () => {
    console.log('🔀 Switching viewer from', currentViewer, 'to', currentViewer === 'microsoft' ? 'google' : 'microsoft');
    setIframeError(false);
    const newViewer = currentViewer === 'microsoft' ? 'google' : 'microsoft';
    setCurrentViewer(newViewer);
    if (publicUrl) {
      updateViewerUrl(publicUrl, newViewer);
    } else {
      console.warn('⚠️  No publicUrl available to update viewer');
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(publicUrl || fileUrl, {
        headers: publicUrl ? {} : (token ? {
          'Authorization': `Bearer ${token}`
        } : {})
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      alert('Failed to download document');
    }
  };

  const handleOpenInNewTab = () => {
    if (viewerUrl) {
      window.open(viewerUrl, '_blank');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center px-3 py-2 bg-gray-50">
          <div className="flex items-center space-x-3">
            <h3 className="text-sm font-semibold text-gray-900 truncate max-w-md">{fileName}</h3>
          </div>
          
          {/* Controls */}
          <div className="flex items-center space-x-1">
            {!loading && (
              <button
                onClick={switchViewer}
                className="px-2 py-1.5 text-xs rounded-md bg-cyan-600 text-white hover:bg-cyan-700 transition-colors flex items-center space-x-1"
                title={`Switch to ${currentViewer === 'microsoft' ? 'Google' : 'Microsoft'} Viewer`}
              >
                <RefreshCw className="h-3 w-3" />
                <span>{currentViewer === 'microsoft' ? 'Try Google' : 'Try Microsoft'}</span>
              </button>
            )}
            
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
              title="Download Document"
            >
              <Download className="h-4 w-4" />
            </button>
            
            <button
              onClick={handleOpenInNewTab}
              className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
              title="Open in New Tab"
              disabled={!viewerUrl}
            >
              <ExternalLink className="h-4 w-4" />
            </button>
            
            <button
              onClick={onClose}
              className="p-1.5 rounded-md hover:bg-gray-200 transition-colors"
              title="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Office Document Viewer */}
        <div className="flex-1 overflow-hidden bg-gray-100" style={{ position: 'relative' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mb-4 mx-auto"></div>
                <p className="text-gray-600">Loading document viewer...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center p-8 max-w-2xl">
                <div className="mb-6">
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-blue-100 rounded-full mb-4">
                    <svg className="w-10 h-10 text-theme-blue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{fileName}</h3>
                  <p className="text-sm text-gray-600 mb-6">
                    Unable to display document in viewer. Download to view in Microsoft Office or compatible applications.
                  </p>
                  <p className="text-xs text-red-600 mb-6">{error}</p>
                </div>
                
                <button
                  onClick={handleDownload}
                  className="px-8 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors inline-flex items-center space-x-3 text-base font-medium"
                >
                  <Download className="h-5 w-5" />
                  <span>Download Document</span>
                </button>
              </div>
            </div>
          ) : (
            <div style={isPowerPoint ? { 
              width: '100%', 
              height: '100%',
            } : { 
              width: '100%', 
              height: 'calc(100% + 60px)', 
              marginBottom: '-60px',
              overflow: 'hidden'
            }}>
              <iframe
                src={viewerUrl}
                className="w-full h-full"
                title={fileName}
                style={{ border: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-between items-center px-3 py-2 border-t border-gray-200 bg-gray-50">
          <div className="text-xs text-gray-600">
            {loading ? 'Loading...' : error ? 'Click download to view document' : '\u00A0'}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OfficeViewer;
